import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Input, Button, Password, TooltipTrigger, AvatarPicker } from '@bcmi-labs/react-components';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import InfoIcon from '../../../common/InfoIcon';
import { IconOperationResetArrow } from '@bcmi-labs/icons';
import AlertBox from '../../../common/AlertBox';
import LoadingSpinner from '../../../common/LoadingSpinner';

import './RegisterFormCoppa.scss';

const RegisterFormCoppa = (props) => {
  const { t } = useTranslation('registerChild');

  const { handleSubmit, control, errors, formState, setValue } = useForm({
    mode: 'onBlur',
  });
  const { onSubmit } = props;

  const [username, setUsername] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [avatarList, setAvatarList] = useState(null);
  const [selectedAvatar, setSelectedAvatar] = useState(null);

  const fetchUsername = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/v1/children/username`);
      if (response.ok) {
        const result = await response.json();
        return result;
      } else {
        throw new Error('Cannot fetch username');
      }
    } catch (e) {
      throw new Error('Cannot fetch username');
    }
  };

  const fetchAvatarList = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/v1/children/avatars`);
      if (response.ok) {
        const result = await response.json();

        return result.map((av) => {
          return {
            name: av.id,
            avatar: `data:image/svg+xml;base64, ${av.data}`,
          };
        });
      } else {
        throw new Error('Cannot fetch avatar list');
      }
    } catch (e) {
      throw new Error('Cannot fetch avatar list');
    }
  };

  useEffect(() => {
    (async function setDefaultValues() {
      try {
        Promise.all([fetchUsername(), fetchAvatarList()]).then((results) => {
          setUsername(results[0]);
          setSelectedAvatar(results[1][0].name);
          setAvatarList(results[1]);
        });
      } catch (e) {
        setFetchError(e);
      }
    })();
  }, []);

  if (!username || !avatarList) {
    return (
      <section className="register-child">
        <LoadingSpinner></LoadingSpinner>
      </section>
    );
  }

  if (fetchError && (!username || !avatarList)) {
    return (
      <section className="register-child">
        <h1 className="arduino-typography-large">{t('registerChild:title')}</h1>
        <p className="description">{t('registerChild:description')}</p>

        <AlertBox message={fetchError} />
        <div className="spacer"></div>
      </section>
    );
  }

  const submitWrapper = (data) => {
    onSubmit({ ...data, avatar: selectedAvatar });
  };

  const changeUsername = async () => {
    try {
      const newUsername = await fetchUsername();
      setValue('username', newUsername);
    } catch (e) {
      setFetchError(e);
    }
  };

  return (
    <section className="register-child">
      <h1 className="arduino-typography-large">{t('registerChild:title')}</h1>

      <p className="description">{t('registerChild:description')}</p>

      <form onSubmit={handleSubmit(submitWrapper)} noValidate>
        <div className="form-row avatar-row">
          <AvatarPicker
            isEdit
            avatar={selectedAvatar}
            avatarList={avatarList}
            modalTitle={t('registerChild:modalTitle')}
            onSelect={(e) => setSelectedAvatar(e)}
          />
        </div>
        <div className="form-row">
          <Controller
            as={Input}
            name="username"
            control={control}
            defaultValue={username}
            buttons={[<IconOperationResetArrow key="icon" onClick={changeUsername} />]}
            label={t('registerChild:username')}
            error={errors.username && errors.username.message}
            isReadOnly
            isRequired
            rules={{
              required: t('registerChild:fieldRequired'),
              pattern: {
                value: /(^[a-z0-9][a-z0-9_-]{2,}$)/i,
                message: t('registerChild:usernameInvalid'),
              },
            }}
          />
          <div className="tooltip-row-wrapper">
            <TooltipTrigger
              TriggerElement={
                <div>
                  <InfoIcon />
                </div>
              }
              className="username-popover"
              tooltipPlacement="top-start"
              tooltipStrategy="absolute"
              tooltipType={'popover'}
            >
              {t('registerChild:tooltip')}
            </TooltipTrigger>
          </div>
        </div>
        <div className="form-row">
          <Controller
            as={Password}
            name="password"
            control={control}
            infoMsg={t('registerChild:passwordMinLength')}
            defaultValue=""
            label={t('registerChild:password')}
            error={errors.password && errors.password.message}
            isRequired
            rules={{
              required: t('registerChild:fieldRequired'),
              minLength: {
                value: 8,
                message: t('registerChild:passwordMinLength'),
              },
            }}
          />
        </div>
        {fetchError ? <AlertBox message={fetchError} /> : null}
        <Button type="submit" isDisabled={!formState.isValid}>
          {t('registerChild:submitText')}
        </Button>
      </form>

      <div className="spacer"></div>
    </section>
  );
};

RegisterFormCoppa.propTypes = {
  onSubmit: PropTypes.func.isRequired,
};

export default RegisterFormCoppa;
